"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconChevronExpandYOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMorphoMarketSearch, fetchMarketById, type MorphoMarket } from "@/hooks/useMorphoMarketSearch";
import { MORPHO_CHAIN_NAMES } from "@/hooks/useMorphoVaultSearch";
import { cn, formatAddress } from "@/lib/utils";
import { formatApy, formatUsd } from "@/lib/utils/morphoFormatters";
import { addRecentMorphoMarket, type RecentMorphoMarket } from "@/lib/utils/recentSelections";
import * as React from "react";
interface SelectedMarketInfo {
  marketId: string;
  chainId: number;
  collateralSymbol: string;
  loanSymbol: string;
  loanAddress: string;
  lltvPercent: number;
  borrowApy: number;
}
function toSelectedInfo(market: MorphoMarket | RecentMorphoMarket): SelectedMarketInfo {
  return {
    marketId: market.marketId,
    chainId: market.chainId,
    collateralSymbol: market.collateralSymbol,
    loanSymbol: market.loanSymbol,
    loanAddress: market.loanAddress,
    lltvPercent: market.lltvPercent,
    borrowApy: market.borrowApy
  };
}
interface MorphoMarketSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string | undefined;
  onChange: (value: string) => void;
  chainId?: number;
  loanAssetAddress?: string;
  popoverModal?: boolean;
}
function MorphoMarketSelector(t0) {
  const $ = _c(58);
  const {
    label,
    description,
    headerRight,
    value,
    onChange,
    chainId,
    loanAssetAddress,
    popoverModal: t1
  } = t0;
  const popoverModal = t1 === undefined ? false : t1;
  const listboxId = React.useId();
  const [containerEl, setContainerEl] = React.useState(null);
  const searchInputRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [selectedMarket, setSelectedMarket] = React.useState(null);
  let t2;
  if ($[0] !== chainId || $[1] !== loanAssetAddress || $[2] !== open) {
    t2 = {
      chainId,
      loanAssetAddress,
      open
    };
    $[0] = chainId;
    $[1] = loanAssetAddress;
    $[2] = open;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    setSearchQuery,
    isLoading,
    markets,
    allMarkets,
    recentMarkets,
    refreshRecentMarkets,
    searchQuery
  } = useMorphoMarketSearch(t2);
  const selectedIdRef = React.useRef(null);
  let t3;
  let t4;
  if ($[4] !== allMarkets || $[5] !== chainId || $[6] !== recentMarkets || $[7] !== value) {
    t3 = () => {
      if (!value) {
        selectedIdRef.current = null;
        setSelectedMarket(null);
        return;
      }
      if (selectedIdRef.current === value.toLowerCase()) {
        return;
      }
      const found = allMarkets.find(m => m.marketId.toLowerCase() === value.toLowerCase());
      if (found) {
        selectedIdRef.current = found.marketId.toLowerCase();
        setSelectedMarket(toSelectedInfo(found));
        return;
      }
      const recent = recentMarkets.find(m_0 => m_0.marketId.toLowerCase() === value.toLowerCase());
      if (recent) {
        selectedIdRef.current = recent.marketId.toLowerCase();
        setSelectedMarket(toSelectedInfo(recent));
        return;
      }
      if (/^0x[a-fA-F0-9]{64}$/.test(value)) {
        let cancelled;
        cancelled = false;
        setSelectedMarket(null);
        fetchMarketById(value, chainId).then(market => {
          if (cancelled) {
            return;
          }
          if (market) {
            selectedIdRef.current = market.marketId.toLowerCase();
            setSelectedMarket(toSelectedInfo(market));
          } else {
            selectedIdRef.current = value.toLowerCase();
          }
        }).catch(_temp);
        return () => {
          cancelled = true;
        };
      }
      selectedIdRef.current = null;
      setSelectedMarket(null);
    };
    t4 = [value, allMarkets, recentMarkets, chainId];
    $[4] = allMarkets;
    $[5] = chainId;
    $[6] = recentMarkets;
    $[7] = value;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[10] !== onChange || $[11] !== refreshRecentMarkets) {
    t5 = market_0 => {
      const info = toSelectedInfo(market_0);
      selectedIdRef.current = info.marketId.toLowerCase();
      setSelectedMarket(info);
      onChange(market_0.marketId);
      setOpen(false);
      addRecentMorphoMarket(info);
      refreshRecentMarkets();
    };
    $[10] = onChange;
    $[11] = refreshRecentMarkets;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleSelect = t5;
  let t6;
  if ($[13] !== chainId || $[14] !== handleSelect || $[15] !== value) {
    t6 = (market_1, keyPrefix) => <CommandItem key={`${keyPrefix}-${market_1.chainId}-${market_1.marketId}`} value={`${market_1.chainId}-${market_1.marketId}`} onSelect={() => handleSelect(market_1)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"><div className="flex w-full cursor-pointer items-center gap-2"><IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", value?.toLowerCase() === market_1.marketId.toLowerCase() ? "opacity-100" : "opacity-0")} /><div className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-medium">{market_1.collateralSymbol} / {market_1.loanSymbol}</span><span className="text-ds-text-muted text-xs">LLTV {market_1.lltvPercent.toFixed(0)}%{" \xB7 "}{formatApy(market_1.borrowApy)} borrow APY{"borrowAssetsUsd" in market_1 && market_1.borrowAssetsUsd > 0 && <>{" \xB7 "}{formatUsd(market_1.borrowAssetsUsd)} borrowed</>}{!chainId && " \xB7 " + (MORPHO_CHAIN_NAMES[market_1.chainId] ?? `Chain ${market_1.chainId}`)}</span></div><span className="text-ds-text-disabled flex-shrink-0 font-mono text-xs">{formatAddress(market_1.marketId)}</span></div></CommandItem>;
    $[13] = chainId;
    $[14] = handleSelect;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const renderMarketItem = t6;
  let t7;
  if ($[17] !== headerRight || $[18] !== label) {
    t7 = label && <div className="flex items-center justify-between"><label className="field-label block">{label}</label>{headerRight && <div className="flex items-center gap-3">{headerRight}</div>}</div>;
    $[17] = headerRight;
    $[18] = label;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== description) {
    t8 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[20] = description;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== selectedMarket || $[23] !== value) {
    t9 = selectedMarket ? <span className="flex items-center gap-2"><span className="text-ds-text-primary truncate">{selectedMarket.collateralSymbol} / {selectedMarket.loanSymbol}</span><span className="text-ds-text-muted flex-shrink-0 text-xs">LLTV {selectedMarket.lltvPercent.toFixed(0)}% · {formatApy(selectedMarket.borrowApy)}</span></span> : value ? <span className="text-ds-text-primary truncate font-mono">{formatAddress(value)}</span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select Morpho Blue market...</span>;
    $[22] = selectedMarket;
    $[23] = value;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== listboxId || $[27] !== open || $[28] !== t9) {
    t11 = <PopoverTrigger asChild={true}><button type="button" role="combobox" aria-controls={listboxId} aria-expanded={open} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">{t9}{t10}</button></PopoverTrigger>;
    $[26] = listboxId;
    $[27] = open;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = popoverModal ? containerEl ?? undefined : undefined;
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e => {
      e.preventDefault();
      searchInputRef.current?.focus({
        preventScroll: true
      });
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== searchQuery || $[32] !== setSearchQuery) {
    t14 = <CommandInput ref={searchInputRef} placeholder="Search markets by collateral..." value={searchQuery} onValueChange={setSearchQuery} />;
    $[31] = searchQuery;
    $[32] = setSearchQuery;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== isLoading || $[35] !== markets || $[36] !== recentMarkets || $[37] !== renderMarketItem || $[38] !== searchQuery) {
    t15 = isLoading ? <div className="body-sm py-6 text-center">Loading...</div> : <><CommandEmpty>No markets found.</CommandEmpty>{recentMarkets.length > 0 && !searchQuery && <CommandGroup heading="Recently Used">{recentMarkets.map(market_2 => renderMarketItem(market_2, "recent"))}</CommandGroup>}<CommandGroup heading={recentMarkets.length > 0 && !searchQuery ? "All Markets" : undefined}>{markets.map(market_3 => renderMarketItem(market_3, "all"))}</CommandGroup></>;
    $[34] = isLoading;
    $[35] = markets;
    $[36] = recentMarkets;
    $[37] = renderMarketItem;
    $[38] = searchQuery;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== listboxId || $[41] !== t15) {
    t16 = <CommandList id={listboxId} className="max-h-64">{t15}</CommandList>;
    $[40] = listboxId;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t14 || $[44] !== t16) {
    t17 = <Command shouldFilter={false}>{t14}{t16}</Command>;
    $[43] = t14;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t12 || $[47] !== t17) {
    t18 = <PopoverContent className="bg-ds-surface-card max-h-[var(--radix-popover-content-available-height,400px)] w-[var(--radix-popover-trigger-width)] overflow-hidden p-0" side="bottom" align="start" container={t12} onOpenAutoFocus={t13}>{t17}</PopoverContent>;
    $[46] = t12;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== open || $[50] !== popoverModal || $[51] !== t11 || $[52] !== t18) {
    t19 = <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>{t11}{t18}</Popover>;
    $[49] = open;
    $[50] = popoverModal;
    $[51] = t11;
    $[52] = t18;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t19 || $[55] !== t7 || $[56] !== t8) {
    t20 = <div ref={setContainerEl} className="space-y-2">{t7}{t8}{t19}</div>;
    $[54] = t19;
    $[55] = t7;
    $[56] = t8;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  return t20;
}
function _temp(err) {
  console.warn("[MorphoMarketSelector] Failed to resolve market by ID:", err);
}
export default React.memo(MorphoMarketSelector);